import { Link2, X } from 'lucide-react';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { FormMessage } from '@nucleus-commerce/ui/components/form';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { CollectionPicker } from '@/features/resource-pickers';

import type { ScheduleFormInput, ScheduleStoreForm } from '../schemas/schedule-form-schema';

export interface StoreOption {
  id: string;
  name: string;
  timezone: string;
  /** Closed stores cannot become targets; one that already is stays. */
  closed: boolean;
  /** A target the signed-in member cannot reach: shown, never changed here. */
  locked: boolean;
}

export interface ScheduleStoresFieldProps {
  stores: readonly StoreOption[];
  disabled?: boolean;
}

/**
 * Target stores as a checklist, in the order they were ticked (the first one sets the default
 * time zone). A ticked store can name a collection of its own that rotated-out products redirect
 * to until they are live again.
 */
export function ScheduleStoresField({ stores, disabled = false }: ScheduleStoresFieldProps) {
  const { t } = useTranslation('schedules');
  const form = useFormContext<ScheduleFormInput>();
  const selected = useWatch({ control: form.control, name: 'stores' });
  const [picking, setPicking] = useState<string | null>(null);

  const write = (next: ScheduleStoreForm[]) =>
    form.setValue('stores', next, { shouldDirty: true, shouldValidate: true });
  const toggle = (storeId: string, checked: boolean) =>
    write(
      checked
        ? [...selected, { storeId, redirectCollectionId: null, redirectCollectionTitle: null }]
        : selected.filter((s) => s.storeId !== storeId),
    );
  const setRedirect = (storeId: string, id: string | null, title: string | null) =>
    write(
      selected.map((s) =>
        s.storeId === storeId
          ? { ...s, redirectCollectionId: id, redirectCollectionTitle: title }
          : s,
      ),
    );

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={t('fields.stores')}>
      <ul className="divide-y rounded-md border">
        {stores.map((store) => {
          const index = selected.findIndex((s) => s.storeId === store.id);
          const target = index === -1 ? undefined : selected[index];
          const checkboxId = `schedule-store-${store.id}`;
          const blocked = disabled || store.locked || (store.closed && !target);
          return (
            <li key={store.id} className="flex flex-col gap-2 px-3 py-2.5">
              <div className="flex items-center gap-3">
                <Checkbox
                  id={checkboxId}
                  checked={target !== undefined}
                  disabled={blocked}
                  onCheckedChange={(checked) => toggle(store.id, checked === true)}
                />
                <label
                  htmlFor={checkboxId}
                  className={cn('flex-1 text-sm', blocked && 'text-muted-foreground')}
                >
                  <span className="font-medium">{store.name}</span>
                  <span className="text-muted-foreground ml-2 text-xs">{store.timezone}</span>
                  {index === 0 ? (
                    <span className="text-muted-foreground ml-2 text-xs">{t('stores.first')}</span>
                  ) : null}
                  {store.closed ? (
                    <span className="text-muted-foreground ml-2 text-xs">{t('stores.closed')}</span>
                  ) : null}
                  {store.locked ? (
                    <span className="text-muted-foreground ml-2 text-xs">
                      {t('stores.noAccess')}
                    </span>
                  ) : null}
                </label>
              </div>
              {target ? (
                <div className="ml-7 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{t('stores.redirect')}</span>
                  {target.redirectCollectionId ? (
                    <>
                      <span className="font-medium">
                        {target.redirectCollectionTitle ?? t('stores.someCollection')}
                      </span>
                      {disabled || store.locked ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          aria-label={t('stores.clearRedirect', { store: store.name })}
                          onClick={() => setRedirect(store.id, null, null)}
                        >
                          <X aria-hidden />
                        </Button>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">{t('stores.noRedirect')}</span>
                  )}
                  {disabled || store.locked ? null : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7"
                      onClick={() => setPicking(store.id)}
                    >
                      <Link2 aria-hidden />
                      {t('stores.chooseRedirect')}
                    </Button>
                  )}
                  <FormMessage className="basis-full">
                    {form.getFieldState(`stores.${index}.redirectCollectionId`).error?.message ??
                      form.getFieldState(`stores.${index}.storeId`).error?.message}
                  </FormMessage>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {picking ? (
        <CollectionPicker
          open
          onOpenChange={(open) => {
            if (!open) setPicking(null);
          }}
          storeId={picking}
          multiple={false}
          title={t('stores.redirectPickerTitle')}
          confirmLabel={t('stores.redirectPickerConfirm')}
          initialSelectedIds={
            selected.find((s) => s.storeId === picking)?.redirectCollectionId
              ? [selected.find((s) => s.storeId === picking)?.redirectCollectionId ?? '']
              : []
          }
          onConfirm={(selection) => {
            const collection = selection.items[0];
            setRedirect(picking, collection?.id ?? null, collection?.title ?? null);
            setPicking(null);
          }}
        />
      ) : null}
    </div>
  );
}
